<script>
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import { field, form } from "svelte-forms";
	import ErrorMessageHandler from "../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import Loader from "../../components/Loader/Loader.svelte";
	import PageHeader from "../../components/PageHeader/PageHeader.svelte";
	import client from "../../services/client";
	import {
		errorMessage,
		loading,
		masterData,
		warningModalOpen,
		evMasterData,
		toastMessage,
	} from "../../store/store";
	import SveltyPicker from "svelty-picker";
	import { navigate } from "svelte-routing";
	import { carSeatOptions } from "../../utils/options";
	import {
		dynamicSort,
		extractErrorMessage,
		scrollToTop,
		getOrDefault,
	} from "src/utils/misc";
	import { carDetail } from "../../store/store";
	import { required, min } from "svelte-forms/validators";
	import WarningModal from "../../components/CustomModal/WarningModal.svelte";
	import {
		convertSGDateOnly,
		convertLocalDateOnlyToUtc,
	} from "../../services/helpers.ts";
	import { onDestroy, onMount } from "svelte";
	import LoaderContainer from "../../components/Loader/LoaderContainer.svelte";
	import CustomInput from "../../components/CustomInput/CustomInput.svelte";
	import CustomSelect from "../../components/CustomSelect/CustomSelect.svelte";
	import CustomSearch from "../../components/CustomSearch/CustomSearch.svelte";
	import { cachedMasterData } from "../../services/profile";
	import {
		getTelematicsDetails,
		updateTelematicsDetails,
	} from "../../services/telematics";
	import { getEvMasterData as fetchEvMasterData } from "../../services/evCharging";
	import { getEfficiencyRates } from "../../services/vehicle";
	import { brand } from "src/brand";

	export let isEdit;

	// === Constants ===
	const telemetryOptions = [
		{ value: 1, label: "Cartrack" },
		{ value: 2, label: "Invers" },
	];

	// === Form Initialization ===
	const init = isEdit ? $carDetail : {};
	const getDate = (val) => (val ? convertSGDateOnly(val) : "");

	const vehicleMakeId = field("vehicleMakeId", init.vehicleMakeId ?? "", [
		required(),
	]);
	const vehicleModelId = field("vehicleModelId", init.vehicleModelId ?? "", [
		required(),
	]);
	const numSeats = field("numSeats", init.numSeats ?? "", [required()]);
	const vehicleFuelId = field("vehicleFuelId", init.vehicleFuelId ?? "", [
		required(),
	]);
	const bootSpace = field("bootSpace", init.bootSpace ?? "", [required()]);
	const description = field("description", init.description ?? "", [
		required(),
	]);
	const iuNumber = field("iuNumber", init.iuNumber ?? "", [required()]);
	const priceGroupId = field("priceGroupId", init.priceGroupId ?? "", [
		required(),
	]);
	const engineNumber = field("engineNumber", init.engineNumber ?? "", [
		required(),
	]);
	const vehicleColorId = field("vehicleColorId", init.vehicleColorId ?? "", [
		required(),
	]);
	const assetOwnerId = field("assetOwnerId", init.assetOwnerId ?? "", [
		required(),
	]);
	const plateNumber = field("plateNumber", init.plateNumber ?? "", [
		required(),
	]);
	const chassisNumber = field("chassisNumber", init.chassisNumber ?? "", [
		required(),
	]);
	const currentMileage = field("currentMileage", init.currentMileage ?? "", [
		required(),
	]);
	const stationId = field("stationId", init.stationId ?? "", []);
	const currentFuelLevel = field(
		"currentFuelLevel",
		init.currentFuelLevel ?? "",
		[]
	);
	const lastServiceMileage = field(
		"lastServiceMileage",
		init.lastServiceMileage ?? "",
		[min(0)]
	);
	const lastServiceDate = field(
		"lastServiceDate",
		getDate(init.lastServiceDate),
		[]
	);
	const lastCleanedDate = field(
		"lastCleanedDate",
		getDate(init.lastCleanedDate),
		[]
	);
	const activeFrom = field("activeFrom", getDate(init.activeFrom), [
		required(),
	]);
	const activeTo = field("activeTo", getDate(init.activeTo), [required()]);
	const coeStartDate = field("coeStartDate", getDate(init.coeStartDate), []);
	const coeEndDate = field("coeEndDate", getDate(init.coeEndDate), []);
	const vepExpiryDate = field(
		"vepExpiryDate",
		getDate(init.vepExpiryDate),
		[]
	);
	const telemetryProvider = field("telemetryProvider", "", [required()]);
	const fuelEfficiency = field("fuelEfficiency", init.efficiency ?? "", [
		required(),
	]);
	const fuelCapacity = field("fuelCapacity", init.capacity ?? "", [
		required(),
	]);

	const platformItems = brand.platformOptions.map((o) => ({
		value: o.id,
		label: o.label,
	}));
	const platform = field(
		"platform",
		isEdit
			? platformItems.find((p) => p.value == init.platform)
			: platformItems[0],
		[required()]
	);
	const carPark = field(
		"carPark",
		isEdit ? { value: init.carParkId, label: init.carParkName } : "",
		[required()]
	);

	const myForm = form(
		activeFrom,
		activeTo,
		vehicleMakeId,
		vehicleModelId,
		vehicleFuelId,
		vehicleColorId,
		description,
		plateNumber,
		platform,
		iuNumber,
		chassisNumber,
		engineNumber,
		numSeats,
		bootSpace,
		carPark,
		assetOwnerId,
		priceGroupId,
		lastCleanedDate,
		lastServiceDate,
		currentMileage,
		currentFuelLevel,
		lastServiceMileage,
		telemetryProvider,
		coeStartDate,
		coeEndDate,
		vepExpiryDate
	);

	// === Derived Options ===
	const stationIdOptions = $evMasterData?.evStationIds?.map((item) => {
		return {
			value: item.stationId.toString(),
			label: item.stationId,
		};
	});

	const carParkOptions = $masterData?.carParks?.sort(dynamicSort("name"));
	const loadOptions = carParkOptions.map((item) => {
		return {
			value: item.id,
			label: item.name,
		};
	});

	// === Local State ===
	let evDataFetched = false;
	let fuelDataFetched = false;
	let fuelDataFetching = false;
	let evDataFetching = false;
	let invalidModel;
	let duplicatePlateNo;
	let efficiencyRates;
	let userChangedMakeModelFuel = false;
	let emptyFieldFinderRan = false;

	// === Store Subscriptions ===
	let load = false;
	loading.subscribe((value) => {
		load = value;
	});

	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});

	// === Data Fetching ===
	const getEvMasterData = async () => {
		if (evDataFetched || evDataFetching) return;
		evDataFetching = true;
		try {
			const res = await fetchEvMasterData();
			evMasterData.set(res.data.data);
			evDataFetched = true;
		} catch (error) {
			console.error(error);
		} finally {
			evDataFetching = false;
		}
	};

	const fetchEfficiencyRates = async () => {
		if (fuelDataFetched || fuelDataFetching) return;

		fuelDataFetching = true;
		try {
			efficiencyRates = await getEfficiencyRates();
			fuelDataFetched = true;
		} catch (error) {
			console.error(error);
		} finally {
			fuelDataFetching = false;
		}
	};

	// === Form Logic ===
	const handleMakeModelFuelChange = () => {
		userChangedMakeModelFuel = true;
		emptyFieldFinderRan = false;
		$fuelCapacity.value = null;
		$fuelEfficiency.value = null;
	};

	const emptyFieldFinders = () => {
		const isElectric = fuelTypes?.name === "Electric";

		const foundObject = isElectric
			? ($evMasterData.efficiencyRates ?? []).find(
					(item) =>
						item.vehicleMakeId == $vehicleMakeId.value &&
						item.vehicleModelId == $vehicleModelId.value
				)
			: (efficiencyRates ?? []).find(
					(item) =>
						item.makeId == $vehicleMakeId.value &&
						item.modelId == $vehicleModelId.value &&
						item.fuelId == $vehicleFuelId.value
				);
		$fuelCapacity.value = foundObject
			? ((foundObject.fuelTankSizeOrCapacity || foundObject.capacity) ??
				foundObject.evMaxDistance)
			: null;
		$fuelEfficiency.value = foundObject
			? (foundObject.fuelConsumptionOrEfficiency ??
				foundObject.efficiency)
			: null;
	};

	async function handleSubmit() {
		if ($fuelCapacity.value === null || $fuelEfficiency.value === null) {
			warningModalOpen.set(true);
		} else {
			loading.set(true);
			try {
				let data = {
					plateNumber: $plateNumber.value,
					vehicleMakeId: $vehicleMakeId.value,
					vehicleModelId: $vehicleModelId.value,
					vehicleTypeId: typeInput,
					numberOfSeats: parseInt($numSeats.value),
					fuelTypeId: parseInt($vehicleFuelId.value),
					bootSpace: $bootSpace.value,
					description: $description.value,
					iuNumber: $iuNumber.value,
					priceGroupId: $priceGroupId.value,
					carparkId: $carPark.value.value,
					chassisNumber: $chassisNumber.value,
					engineNumber: $engineNumber.value,
					vehicleColorId: $vehicleColorId.value,
					assetOwnerId: $assetOwnerId.value,
					lastServiceDate:
						$lastServiceDate.value == ""
							? null
							: new Date($lastServiceDate.value),
					lastCleanedDate:
						$lastCleanedDate.value == ""
							? null
							: new Date($lastCleanedDate.value),
					activeFrom: convertLocalDateOnlyToUtc($activeFrom.value),
					activeTo: convertLocalDateOnlyToUtc($activeTo.value),
					coeStartDate: convertLocalDateOnlyToUtc(
						$coeStartDate.value
					),
					coeEndDate: convertLocalDateOnlyToUtc($coeEndDate.value),
					vepExpiryDate: convertLocalDateOnlyToUtc(
						$vepExpiryDate.value
					),
					currentMileage: parseInt($currentMileage.value),
					currentFuelLevel: parseInt($currentFuelLevel.value),
					stationId: $stationId.value?.label?.toString() || null,
					lastServiceMileage: parseInt($lastServiceMileage.value),
					telemetryProvider: parseInt(
						$telemetryProvider.value?.value
					),
				};
				data.platform = parseInt($platform.value.value);
				if (isEdit) {
					data.id = parseInt(isEdit);
				}
				const res = await client("/v1", {
					method: isEdit ? "PUT" : "POST",
					version: "Vehicle",
					data,
				});

				if (res.status === 200) {
					const vehicleId = isEdit || res.data.id;
					const providerLabel = $telemetryProvider.value?.label;
					const plateNo = res.data.plateNumber || $plateNumber.value;
					try {
						await updateTelematicsDetails(
							vehicleId,
							providerLabel,
							plateNo,
							fuelTypes?.name
						);
						toastMessage.set({
							type: "success",
							message: `Car ${
								isEdit ? "updated" : "created"
							} successfully.`,
						});
						navigate(`/car-management/details/${vehicleId}`);
					} catch (e) {
						console.error("Telematics update failed:", e);
						extractErrorMessage(e);
						if (!isEdit) {
							if (e.response.data.code === 22018) {
								navigate(
									`/car-management/details/${vehicleId}`,
									{
										state: {
											telematicsError: $errorMessage,
										},
									}
								);
							} else {
								navigate(
									`/car-management/details/${vehicleId}`
								);
							}
						} else {
							scrollToTop();
						}
					}
				}

				loading.set(false);
			} catch (error) {
				extractErrorMessage(error);
				if (error.response?.data.code === 22010) {
					navigate("/car-management");
				}
				loading.set(false);
			}
		}
	}

	// === Reactive Statements ===
	$: filteredModel =
		$vehicleMakeId.value === ""
			? $masterData.vehicleModels
			: $masterData.vehicleModels.filter((model) => {
					return model.makeId == $vehicleMakeId.value;
				});
	$: selectedModel = $masterData.vehicleModels.find(
		(model) => model.id == $vehicleModelId.value
	);
	$: typeCar = selectedModel ? selectedModel?.typeId : "";
	$: selectedType =
		$masterData.vehicleTypes.find((type) => type.id == typeCar) || "";
	$: type = selectedType ? selectedType.name : "Select type";
	$: typeInput = selectedType ? selectedType.id : "Select type";
	$: fuelTypes = $masterData.fuelTypes.find(
		(type) => type.id == $vehicleFuelId.value
	);

	$: if ($vehicleFuelId.value !== "" && fuelTypes?.name === "Electric") {
		if (!evDataFetched) getEvMasterData();
	}

	$: if (
		$vehicleFuelId.value !== "" &&
		fuelTypes?.name &&
		fuelTypes.name !== "Electric" &&
		!fuelDataFetched
	) {
		fetchEfficiencyRates();
	}
	$: if (
		$vehicleMakeId.value !== "" &&
		$vehicleModelId.value !== "" &&
		$vehicleFuelId.value !== "" &&
		(fuelTypes?.name === "Electric" ? evDataFetched : fuelDataFetched) &&
		!emptyFieldFinderRan &&
		((!init.capacity && !init.efficiency) || userChangedMakeModelFuel)
	) {
		emptyFieldFinders();
		emptyFieldFinderRan = true;
	}

	$: isCoeRangeInvalid =
		!!$coeStartDate.value &&
		!!$coeEndDate.value &&
		new Date($coeStartDate.value) >= new Date($coeEndDate.value);

	$: isInvalid =
		isCoeRangeInvalid ||
		!$activeFrom.value ||
		!$activeTo.value ||
		!$vehicleMakeId.value ||
		!$vehicleModelId.value ||
		!$vehicleFuelId.value ||
		!$vehicleColorId.value ||
		!$description.value ||
		!$plateNumber.value ||
		!$iuNumber.value ||
		!$chassisNumber.value ||
		!$engineNumber.value ||
		!$numSeats.value ||
		!$bootSpace.value ||
		!$carPark.value ||
		!$assetOwnerId.value ||
		!$priceGroupId.value ||
		!$platform.value ||
		!$telemetryProvider.value;

	// === Lifecycle ===
	onMount(async () => {
		await cachedMasterData();
		if (isEdit) {
			try {
				const res = await getTelematicsDetails(isEdit);
				if (res.data && res.data.provider) {
					const option = telemetryOptions.find(
						(o) => o.label === res.data.provider
					);
					$telemetryProvider.value = getOrDefault(option, "");
				} else {
					$telemetryProvider.value = "";
				}
			} catch (e) {
				console.error(e);
				$telemetryProvider.value = "";
			}
		}
	});

	onDestroy(() => {
		carDetail.set({});
	});
</script>

<LoaderContainer>
	<Row>
		<Col>
			<div>
				<PageHeader
					title={isEdit ? "Update Car Details" : "Add New Car"}
				/>
				<ErrorMessageHandler message={err} />

				<Row class="mb-3">
					<Col xs={6}>
						<label for="plateNumber" class="d-block mb-1"
							><b>Car Plate No.*</b></label
						>
						<CustomInput
							type="text"
							bind:value={$plateNumber.value}
							validate={required()}
							error={$myForm.hasError("plateNumber.required") ||
								duplicatePlateNo}
							errorText={$myForm.hasError("plateNumber.required")
								? "Car plate number is required"
								: duplicatePlateNo
									? "This plate number is already added"
									: ""}
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="platform" class="d-block mb-1"
							><b>Platform*</b></label
						>
						<CustomSearch
							bind:value={$platform.value}
							items={platformItems}
							isSearchable={false}
							error={$myForm.hasError("platform.required")}
							errorText="Platform is required"
							placeholder="Select Platform"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="priority" class="d-block mb-1"
							><b>Car Make*</b></label
						>
						<CustomSelect
							bind:value={$vehicleMakeId.value}
							type="text"
							bind:options={$masterData.vehicleMakes}
							error={$myForm.hasError("vehicleMakeId.required")}
							errorText="Car make is required"
							defaultOption="Select car make"
							onChange={handleMakeModelFuelChange}
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="vehicleModelId" class="d-block mb-1"
							><b>Car Model*</b></label
						>
						<CustomSelect
							bind:value={$vehicleModelId.value}
							type="text"
							options={filteredModel}
							error={$myForm.hasError(
								"vehicleModelId.required"
							) || invalidModel}
							errorText={$myForm.hasError(
								"vehicleModelId.required"
							)
								? "Car model is required"
								: invalidModel
									? "Invalid model"
									: ""}
							defaultOption="Select car model"
							onChange={() => {
								invalidModel = "";
								handleMakeModelFuelChange();
							}}
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="carType" class="d-block mb-1"
							><b>Car Type*</b></label
						>
						<CustomInput
							type="text"
							placeholder={selectedType
								? selectedType.name
								: "Select car type"}
							error={$myForm.hasError("carType.required")}
							errorText="Car type is required"
							disabled={true}
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="telemetryProvider" class="d-block mb-1"
							><b>Telematics Provider*</b></label
						>
						<CustomSearch
							bind:value={$telemetryProvider.value}
							items={telemetryOptions}
							isSearchable={false}
							isClearable={false}
							error={$myForm.hasError(
								"telemetryProvider.required"
							)}
							errorText="Telematics provider is required"
							placeholder="Select telematics provider"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="numSeats" class="d-block mb-1"
							><b>No. of Seats*</b></label
						>
						<CustomSelect
							bind:value={$numSeats.value}
							type="text"
							options={carSeatOptions}
							error={$myForm.hasError("numSeats.required")}
							errorText="No. of seats is required"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="vehicleFuelId" class="d-block mb-1"
							><b>Fuel Type*</b></label
						>
						<div>
							{#each $masterData.fuelTypes as { id, name }, key}
								<label
									key={`option-${name}-${key + 1}`}
									class="customCheckbox"
								>
									<input
										type="radio"
										{name}
										value={id}
										class="d-none input"
										bind:group={$vehicleFuelId.value}
										on:change={handleMakeModelFuelChange}
									/>
									<span class="customCheckbox__content mt-2"
										>{name}</span
									>
								</label>
							{/each}
							{#if $myForm.hasError("vehicleFuelId.required")}
								<div>
									<p class="error-text">
										Fuel Type is required
									</p>
								</div>
							{/if}
						</div>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="bootSpace" class="d-block mb-1"
							><b>Boot Space.*</b></label
						>
						<CustomInput
							type="text"
							bind:value={$bootSpace.value}
							validate={required()}
							error={$myForm.hasError("bootSpace.required")}
							errorText="Bootspace is required"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="description" class="d-block mb-1"
							><b>Description*</b></label
						>
						<CustomInput
							type="text"
							bind:value={$description.value}
							validate={required()}
							error={$myForm.hasError("description.required")}
							errorText="Description is required"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="iuNumber" class="d-block mb-1"
							><b>IU Number*</b></label
						>
						<CustomInput
							type="text"
							bind:value={$iuNumber.value}
							validate={required()}
							error={$myForm.hasError("iuNumber.required")}
							errorText="IU Number is required"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="chassisNumber" class="d-block mb-1"
							><b>Chassis Number*</b></label
						>
						<CustomInput
							type="text"
							bind:value={$chassisNumber.value}
							validate={required()}
							error={$myForm.hasError("chassisNumber.required")}
							errorText="Chasis Number is required"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="engineNumber" class="d-block mb-1"
							><b>Engine Number*</b></label
						>
						<CustomInput
							type="text"
							bind:value={$engineNumber.value}
							validate={required()}
							error={$myForm.hasError("engineNumber.required")}
							errorText="Engine Number is required"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="bodyColor" class="d-block mb-1"
							><b>Body Color*</b></label
						>
						<CustomSelect
							bind:value={$vehicleColorId.value}
							type="text"
							options={$masterData.vehicleColors}
							error={$myForm.hasError("vehicleColorId.required")}
							errorText="Body color is required"
							defaultOption="Select color"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="priceGroupId" class="d-block mb-1"
							><b>Price Group*</b></label
						>
						<div>
							{#each $masterData.priceGroups as { id, name }, key}
								<label
									key={`option-${name}-${key + 1}`}
									class="customCheckbox mt-2"
								>
									<input
										type="radio"
										{name}
										value={id}
										class="d-none input"
										bind:group={$priceGroupId.value}
									/>
									<span class="customCheckbox__content"
										>{name}</span
									>
								</label>
							{/each}
							{#if $myForm.hasError("priceGroupId.required")}
								<div>
									<p class="error-text">
										Price group is required
									</p>
								</div>
							{/if}
						</div>
					</Col>
				</Row>

				{#if $vehicleModelId.value !== "" && $vehicleMakeId.value !== "" && fuelTypes?.name === "Electric"}
					<Row class="mb-3">
						<Col xs={6}>
							<label for="stationId" class="d-block mb-1"
								><b>Station ID</b></label
							>
							<CustomSearch
								bind:value={$stationId.value}
								items={stationIdOptions}
								placeholder="Select station ID"
								handleClear={() => ($stationId.value = "")}
							/>
						</Col>
					</Row>
				{/if}

				<Row class="mb-3">
					<Col xs={6}>
						<label for="carPark" class="d-block mb-1"
							><b>Carpark*</b></label
						>
						<CustomSearch
							bind:value={$carPark.value}
							items={loadOptions}
							error={$myForm.hasError("carPark.required")}
							errorText="Carpark is required"
							placeholder="Select Carpark"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="assetOwnerId" class="d-block mb-1"
							><b>Car Asset Owner*</b></label
						>
						<CustomSelect
							bind:value={$assetOwnerId.value}
							type="text"
							options={$masterData.carAssetOwners}
							error={$myForm.hasError("assetOwnerId.required")}
							errorText="Car Asset owner is required"
							defaultOption="Select Car Asset Owner"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={3}>
						<label for="lastCleanedDate" class="d-block mb-1"
							><b>Last Cleaned</b></label
						>
						<SveltyPicker
							inputClasses="form-control getgo input {new Date(
								$lastCleanedDate.value
							) > new Date()
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$lastCleanedDate.value}
						/>
						{#if new Date($lastCleanedDate.value) > new Date()}
							<div>
								<p class="error-text">
									Last cleaned date cannot be in the future
								</p>
							</div>
						{/if}
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={3}>
						<label for="activeFrom" class="d-block mb-1"
							><b>Start Date*</b></label
						>
						<SveltyPicker
							inputClasses="form-control getgo input {$myForm.hasError(
								'activeFrom.required'
							)
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$activeFrom.value}
						/>
						{#if $myForm.hasError("activeFrom.required")}
							<div>
								<p class="error-text">Start date is required</p>
							</div>
						{:else if new Date($activeFrom.value) > new Date($activeTo.value)}
							<div>
								<p class="error-text">
									Start date cannot be more than end date!
								</p>
							</div>
						{/if}
					</Col>
					<Col xs={3}>
						<label for="activeTo" class="d-block mb-1"
							><b>End Date*</b></label
						>
						<SveltyPicker
							inputClasses="form-control getgo input {$myForm.hasError(
								'activeTo.required'
							)
								? 'danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$activeTo.value}
						/>
						{#if $myForm.hasError("activeTo.required")}
							<div>
								<p class="error-text">End date is required</p>
							</div>
						{/if}
					</Col>
				</Row>

				{#if $vehicleModelId.value !== "" && $vehicleMakeId.value !== ""}
					<Row class="mb-3">
						<Col xs={6}>
							<label for="fuelCapacity" class="d-block mb-1"
								><b>Fuel (L)/Battery Capacity (kWh)</b></label
							>
							<CustomInput
								type="number"
								bind:value={$fuelCapacity.value}
								validate={required()}
								error={$vehicleModelId.value !== "" &&
									!$fuelCapacity.value}
								errorText="This field is empty, please add this data via Master Data > Vehicle Master Data > Car Efficiency"
								placeholder={"No Data"}
								onInput={(e) => {
									if (!e.target.validity.valid)
										e.target.value = "";
								}}
								disabled
							/>
						</Col>
					</Row>
					<Row class="mb-3">
						<Col xs={6}>
							<label for="fuelEfficiency" class="d-block mb-1"
								><b>Fuel/Battery Efficiency (km)</b></label
							>
							<CustomInput
								type="number"
								bind:value={$fuelEfficiency.value}
								validate={required()}
								error={$vehicleModelId.value !== "" &&
									!$fuelEfficiency.value}
								errorText="This field is empty, please add this data via Master Data > Vehicle Master Data > Car Efficiency"
								placeholder={"No Data"}
								onInput={(e) => {
									if (!e.target.validity.valid)
										e.target.value = "";
								}}
								disabled
							/>
						</Col>
					</Row>
				{/if}

				<Row class="mb-3">
					<Col xs={6}>
						<label for="currentMileage" class="d-block mb-1"
							><b>Current Mileage</b></label
						>
						<CustomInput
							type="number"
							bind:value={$currentMileage.value}
							min="0"
							onInput="validity.valid||(value='');"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="currentFuel" class="d-block mb-1"
							><b>Current Fuel Level</b></label
						>
						<CustomInput
							type="number"
							bind:value={$currentFuelLevel.value}
							min="0"
							step="1"
							onInput="validity.valid||(value='');"
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={6}>
						<label for="lastServiced" class="d-block mb-1"
							><b>Last Serviced Mileage</b></label
						>
						<CustomInput
							type="number"
							bind:value={$lastServiceMileage.value}
							error={$myForm.hasError("lastServiceMileage.min")}
							errorText={$myForm.hasError(
								"lastServiceMileage.min"
							)
								? "Input should only be a positive number"
								: ""}
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={3}>
						<label for="lastServiceDate" class="d-block mb-1"
							><b>Last Serviced Date</b></label
						>
						<SveltyPicker
							inputClasses="form-control getgo input {new Date(
								$lastServiceDate.value
							) > new Date()
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$lastServiceDate.value}
						/>
						{#if new Date($lastServiceDate.value) > new Date()}
							<div>
								<p class="error-text">
									Last service date cannot be in the future
								</p>
							</div>
						{/if}
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={3}>
						<label for="coeStartDate" class="d-block mb-1"
							><b>COE Start Date</b></label
						>
						<SveltyPicker
							inputId="coeStartDate"
							inputClasses="form-control getgo input {isCoeRangeInvalid
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$coeStartDate.value}
						/>
						{#if isCoeRangeInvalid}
							<div>
								<p class="error-text">
									COE End date must be after COE Start date!
								</p>
							</div>
						{/if}
					</Col>
					<Col xs={3}>
						<label for="coeEndDate" class="d-block mb-1"
							><b>COE End Date</b></label
						>
						<SveltyPicker
							inputId="coeEndDate"
							inputClasses="form-control getgo input"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$coeEndDate.value}
						/>
					</Col>
				</Row>

				<Row class="mb-3">
					<Col xs={3}>
						<label for="vepExpiryDate" class="d-block mb-1"
							><b>VEP Expiry Date</b></label
						>
						<SveltyPicker
							inputId="vepExpiryDate"
							inputClasses="form-control getgo input"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$vepExpiryDate.value}
						/>
					</Col>
				</Row>

				<Row class="mt-4">
					<Col class="d-flex justify-content-end gap-3">
						<Button class="themeButton" on:click={myForm.reset}
							>Reset</Button
						>
						<span
							class="d-inline-block"
							data-bs-toggle={!$myForm.valid ? "tooltip" : ""}
							title={!$myForm.valid
								? "Fill all required field"
								: ""}
						>
							<Button
								class="themeButton primary"
								disabled={new Date($lastCleanedDate.value) >
									new Date() ||
									new Date($lastServiceDate.value) >
										new Date() ||
									isInvalid}
								type="submit"
								on:click={handleSubmit}
								on:click={myForm.validate}
							>
								{#if load}
									<Loader />
								{:else if isEdit}
									Update
								{:else}
									Create
								{/if}
							</Button>
						</span>
					</Col>
				</Row>
			</div>
		</Col>
	</Row>
</LoaderContainer>

<WarningModal createCarError={true} />
